/**
 * RuleStep - one numbered step in the Assurance Rules tab.
 *
 * The tab is a sequence, not a pile of settings: the calculation mode decides
 * whether a base value is needed at all and what units the bands are then in,
 * so the controls only make sense read in order. Numbering says that, and the
 * connector rail shows how far through you are.
 *
 * Built on the same `TimelineShell` as the card's activity tab, so the two
 * rails in this panel are visibly the same device.
 */
import type { ReactNode } from 'react';
import { InfoIcon } from '@phosphor-icons/react';
import { TimelineShell } from 'src/features/pam/PamActivityTimeline';
import Tooltip from 'src/components/ui/Tooltip';

interface RuleStepProps {
  /** 1-based position, derived from the steps this mode actually needs. */
  number: number;
  title: string;
  /** Extra explanation for the step, shown on the info icon next to the title. */
  info?: string;
  isLast: boolean;
  children: ReactNode;
}

export default function RuleStep({ number, title, info, isLast, children }: RuleStepProps) {
  return (
    <TimelineShell
      isLast={isLast}
      icon={
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border-default bg-white text-xs font-semibold text-content-subtle lining-nums">
          {number}
        </span>
      }
    >
      <div className="flex flex-col gap-2 pb-5">
        <span className="pt-1 text-sm font-semibold text-content-default inline-flex items-center gap-1.5">
          {title}
          {info ? (
            <Tooltip title={info} arrow placement="right">
              <span className="inline-flex cursor-help text-content-subtlest" aria-label={`${title} explained`}>
                <InfoIcon size={14} weight="bold" />
              </span>
            </Tooltip>
          ) : null}
        </span>
        {children}
      </div>
    </TimelineShell>
  );
}
